import React from 'react';
import { createRoot } from 'react-dom/client';
import css from '../widget/widget.css?inline';
import { SiteChatController, type SiteChatEvent } from '../runtime/controller';
import { SiteChatApp } from '../runtime/SiteChatApp';

export { CHAT_GLYPH } from '../widget/glyph';

export type SchoolChatOptions = {
    /** AccelChat address — the same host School's old chat talks to, without `/hub`. */
    apiUrl: string;
    /** The signed-in user's platform token; read on every session request, so a rotated token is picked up. */
    getToken: () => string | null | undefined;
    /**
     * `school` (default): a student in the membership area — their in-app support thread. `admin`: a school admin in
     * the admin panel — AXL's own support channel (404 while it is not configured: keep the old support chat).
     */
    session?: 'school' | 'admin';
};

/** What School's header button needs from the chat. */
export type SchoolChatHandle = {
    /** `true` once the school's membership chat answered; `false` = none, School keeps its old chat. */
    ready: Promise<boolean>;
    readonly isOpen: boolean;
    readonly unread: number;
    /** The launcher image from the channel's settings, for School's own header button (null = default glyph). */
    readonly logo: string | null;
    /** `focus`: the cursor goes into the composer once the thread is drawn. */
    open(options?: { focus?: boolean }): void;
    close(): void;
    toggle(): void;
    /** Called on every change of `isOpen` / `unread`. */
    subscribe(listener: () => void): () => void;
    /** Data for the operator (merged; an empty value removes a key), sent with the next message. */
    setData(values: Record<string, unknown>): void;
    /** Text in the composer; the student or admin still sends it. */
    setText(text: string): void;
    on(event: SiteChatEvent, listener: (payload?: unknown) => void): () => void;
    dispose(): void;
};

/**
 * The website chat in its membership-area mode: the panel of the school's membership chat (the website chat
 * channel marked for the membership area) for a signed-in student, without the launcher — School's own header
 * button opens it. Rendered in a Shadow DOM like the site loader, so School's CSS and the widget's never meet.
 * Nothing is attached to the page until the server confirms the school has such a chat.
 */
export function mountSchoolChat(options: SchoolChatOptions): SchoolChatHandle {
    const controller = new SiteChatController({
        channel: '',
        apiUrl: options.apiUrl,
        mode: 'school',
        getSchoolToken: options.getToken,
        session: options.session ?? 'school',
        win: window,
    });

    let host: HTMLDivElement | null = null;
    let root: ReturnType<typeof createRoot> | null = null;
    let disposed = false;

    const ready = controller.start().then(() => {
        if (disposed || controller.status !== 'ready')
            return false;
        host = document.createElement('div');
        host.setAttribute('data-axl-site-chat', 'school');
        // Above School's antd layers (notifications sit at 2050): on a phone the panel is full-screen and a toast
        // must not cover its composer.
        host.style.cssText = 'all: initial; position: fixed; z-index: 2100; top: 0; left: 0; width: 0; height: 0;';
        document.body.appendChild(host);
        const shadow = host.attachShadow({ mode: 'open' });
        const style = document.createElement('style');
        style.textContent = `:host { all: initial; }\n${css}`;
        shadow.appendChild(style);
        const container = document.createElement('div');
        shadow.appendChild(container);
        root = createRoot(container);
        root.render(React.createElement(SiteChatApp, { controller, win: window }));
        return true;
    });

    return {
        ready,
        get isOpen() {
            return controller.isOpen;
        },
        get unread() {
            return controller.unread;
        },
        get logo() {
            return controller.logo;
        },
        open: options => controller.open(options),
        close: () => controller.close(),
        toggle: () => controller.toggle(),
        subscribe: listener => controller.subscribe(listener),
        setData: values => controller.setData(values),
        setText: text => controller.setText(text),
        on: (event, listener) => controller.on(event, listener),
        dispose: () => {
            disposed = true;
            controller.dispose();
            root?.unmount();
            host?.remove();
        },
    };
}

/**
 * The admin panel's support chat (in place of Chatra): the same panel for a school admin, talking to AXL's own
 * support channel. `ready` resolves false while that channel is not configured — keep the old chat then.
 */
export function mountAdminChat(options: Omit<SchoolChatOptions, 'session'>): SchoolChatHandle {
    return mountSchoolChat({ ...options, session: 'admin' });
}
